import { baseUrl, getSessionAuthentication, rms2BasePath } from '../common/Api';

interface FetchOptions {
    method: string;
    headers: Record<string, string>;
    body?: string;
}

function getHeaders(): Record<string, string> {
    const auth = getSessionAuthentication();
    return {
        'Content-Type': 'application/json',
        'Authorization': auth.token,
        'x-cleverapps-tenant': auth.tenantStringIdentifier
    };
}

async function rms2Fetch<T>(path: string, options?: Partial<FetchOptions>): Promise<T | null> {
    const response = await fetch(rms2BasePath + path, {
        method: options?.method ?? 'GET',
        headers: getHeaders(),
        body: options?.body
    });
    if (!response.ok) {
        const text = await response.text();
        let message = `API error: ${response.status} ${response.statusText}`;
        try {
            const parsed = JSON.parse(text);
            if (parsed?.message) message = parsed.message;
        } catch { /* ignore */ }
        throw new Error(message);
    }
    const text = await response.text();
    return text ? JSON.parse(text) : null;
}

async function legacyFetch<T>(path: string): Promise<T | null> {
    const response = await fetch(baseUrl + path, {
        method: 'GET',
        headers: getHeaders()
    });
    if (!response.ok) {
        const text = await response.text();
        let message = `API error: ${response.status} ${response.statusText}`;
        try {
            const parsed = JSON.parse(text);
            if (parsed?.message) message = parsed.message;
        } catch { /* ignore */ }
        throw new Error(message);
    }
    const text = await response.text();
    return text ? JSON.parse(text) : null;
}

export interface Language {
    id: number;
    name: string;
    locale?: string;
}

export interface Localization {
    language: Language;
    name?: string;
}

export interface PrintDocumentTemplate {
    id: number;
    description: string;
}

export interface DeliveryPlanningTemplate {
    id: number;
    deliveryPlanningTemplateName: string;
}

export interface OrderType {
    id: number;
    orderTypeName: string;
    printDocumentTemplate: PrintDocumentTemplate | null;
    pickingListDocumentTemplate: PrintDocumentTemplate | null;
    deliveryPlanningTemplate: DeliveryPlanningTemplate | null;
    deliveryPlanningTemplateId?: number | null;
    localizations: Localization[];
}

export async function getOrderType(id: number): Promise<OrderType> {
    const result = await rms2Fetch<OrderType>(`ordertypes/${id}`);
    if (!result) throw new Error('Empty response for getOrderType');
    return result;
}

export async function createOrderType(ot: Partial<OrderType>): Promise<OrderType> {
    const result = await rms2Fetch<OrderType>(`ordertypes/${ot.id}`, {
        method: 'PUT',
        body: JSON.stringify({
            orderTypeName: ot.orderTypeName,
            printDocumentTemplateId: ot.printDocumentTemplate?.id ?? null,
            pickingListDocumentTemplateId: ot.pickingListDocumentTemplate?.id ?? null,
            deliveryPlanningTemplateId: ot.deliveryPlanningTemplateId ?? null,
            localizations: ot.localizations?.map((l) => ({
                languageId: l.language.id,
                name: l.name
            })) ?? []
        })
    });
    if (!result) throw new Error('Empty response for createOrderType');
    return result;
}

export async function updateOrderType(ot: OrderType): Promise<OrderType> {
    const result = await rms2Fetch<OrderType>(`ordertypes/${ot.id}`, {
        method: 'PUT',
        body: JSON.stringify({
            orderTypeName: ot.orderTypeName,
            printDocumentTemplateId: ot.printDocumentTemplate?.id ?? null,
            pickingListDocumentTemplateId: ot.pickingListDocumentTemplate?.id ?? null,
            deliveryPlanningTemplateId: ot.deliveryPlanningTemplateId ?? null,
            localizations: ot.localizations?.map((l) => ({
                languageId: l.language.id,
                name: l.name
            })) ?? []
        })
    });
    if (!result) throw new Error('Empty response for updateOrderType');
    return result;
}

// eslint-disable-next-line require-await
export async function deleteOrderType(id: number): Promise<void> {
    throw new Error('Delete not supported for order types');
}

export async function getLanguages(): Promise<Language[]> {
    const response = await rms2Fetch<{ languages: Language[] }>('languages');
    return response?.languages ?? [];
}

export async function getPrintDocumentTemplates(): Promise<PrintDocumentTemplate[]> {
    const response = await rms2Fetch<{ printDocumentTemplates: PrintDocumentTemplate[] }>('ordertypes/printDocumentTemplates');
    return response?.printDocumentTemplates ?? [];
}

export async function getDeliveryPlanningTemplates(): Promise<{ deliveryPlanningTemplates: DeliveryPlanningTemplate[] }> {
    const result = await rms2Fetch<{ deliveryPlanningTemplates: DeliveryPlanningTemplate[] }>('ordertypes/deliveryPlanningTemplates');
    if (!result) throw new Error('Empty response for getDeliveryPlanningTemplates');
    return result;
}

// This endpoint is on the old orders module — keep on legacyFetch
export async function isDeliveryPlanningEnabled(): Promise<boolean> {
    return (await legacyFetch<boolean>('orders/isdeliveryplanningenabled')) ?? false;
}
